<template>
  <div class="agent-checkout">
    <div class="container">
      <h1>Finish this order</h1>
      <p class="lede">
        An assistant put this together. Nothing is made or charged until you say who it's for and pay.
        The assistant never sees a card or an address.
      </p>

      <Spinner :show="loading" message="Loading the cart..." />
      <div v-if="error" class="notice error">{{ error }}<span v-if="details.length"> — {{ details.join('; ') }}</span></div>

      <template v-if="!loading && cart">
        <section class="section" v-if="paid">
          <div class="notice ok">
            <strong>Paid.</strong> Thank you.
            <span v-if="cart.orderIds.length">Order<span v-if="cart.orderIds.length > 1">s</span> {{ cart.orderIds.join(', ') }} are on their way through our print partners.</span>
            <span v-else>Your order is being recorded; this page will update.</span>
          </div>
        </section>

        <section class="section">
          <h2>In the cart</h2>
          <ul class="lines">
            <li v-for="(l, i) in cart.items" :key="i" class="line">
              <div class="line-body">
                <strong>{{ l.displayName }}</strong> <span class="muted">× {{ l.quantity }} · {{ l.estimatedDelivery }}</span>
                <template v-if="!paid && editable(l).length">
                  <div v-for="f in editable(l)" :key="f" class="field">
                    <label :for="`line-${i}-${f}`">{{ fieldLabel(f) }}</label>
                    <textarea v-if="f !== 'senderName' && f !== 'title'" :id="`line-${i}-${f}`" v-model="edits[i][f]" rows="3" :disabled="busy"></textarea>
                    <input v-else type="text" :id="`line-${i}-${f}`" v-model="edits[i][f]" :disabled="busy" />
                  </div>
                </template>
                <div v-else-if="preview(l)" class="preview">“{{ preview(l) }}”</div>
              </div>
              <div class="amount">{{ money(l.amountCents, l.currency) }}</div>
            </li>
          </ul>
          <div class="total"><span>Total</span><strong>{{ money(cart.totalCents, cart.currency) }}</strong></div>
        </section>

        <section class="section" v-if="confirming">
          <div class="notice ok"><strong>Confirming your payment…</strong> This takes a few seconds.</div>
        </section>

        <section class="section" v-else-if="!paid && cart.status !== 'expired'">
          <h2>Who it's for</h2>
          <div class="tabs">
            <button class="tab" :class="{ on: mode === 'address' }" @click="mode = 'address'">I know their address</button>
            <button class="tab" :class="{ on: mode === 'token' }" @click="mode = 'token'">I have a delivery token</button>
          </div>

          <div v-if="mode === 'address'" class="form">
            <input type="text" v-model="recipient.recipientName" placeholder="Their full name" :disabled="busy" />
            <input type="text" v-model="recipient.recipientAddress.street" placeholder="Street address" :disabled="busy" />
            <input type="text" v-model="recipient.recipientAddress.optional" placeholder="Apt, unit (optional)" :disabled="busy" />
            <div class="row3">
              <input type="text" v-model="recipient.recipientAddress.city" placeholder="City" :disabled="busy" />
              <input type="text" v-model="recipient.recipientAddress.state" placeholder="State" :disabled="busy" maxlength="20" />
              <input type="text" v-model="recipient.recipientAddress.postalCode" placeholder="ZIP" :disabled="busy" maxlength="10" />
            </div>
            <input type="text" v-model="recipient.senderName" placeholder="Your name (how it's signed)" :disabled="busy" />
          </div>
          <div v-else class="form">
            <p class="muted">A delivery token comes from the recipient's own agent when it accepted an offer. Their address stays with them.</p>
            <input type="text" v-model="recipientToken" placeholder="Delivery token" :disabled="busy" />
          </div>

          <input type="email" v-model="email" placeholder="Your email, for the receipt" :disabled="busy" />
          <button class="button primary" @click="pay" :disabled="busy || !canPay">{{ busy ? 'One moment…' : `Pay ${money(cart.totalCents, cart.currency)}` }}</button>
          <p class="fine">Content is checked before printing. If the recipient's agent has said no to this kind of thing, you'll be told here and nothing is charged.</p>
        </section>

        <section class="section" v-else-if="cart.status === 'expired'">
          <div class="notice error">This cart has expired. Ask the assistant to make a new one.</div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/SpinnerComp.vue';
import { gatewayGet, gatewayPost } from '@/config/gateway';

export default {
  name: 'AgentCheckoutPage',
  components: { Spinner },
  data() {
    return {
      loading: true, busy: false, error: null, details: [],
      cart: null, mode: 'address', recipientToken: '', email: '',
      recipient: { recipientName: '', recipientAddress: { street: '', optional: '', city: '', state: '', postalCode: '', country: 'US' }, senderName: '' },
      edits: [],
      pollTimer: null,
      pollsLeft: 0,
    };
  },
  computed: {
    cartId() {
      return this.$route.params.cartId;
    },
    paid() {
      return Boolean(this.cart && this.cart.paid);
    },
    // Back from Stripe and the webhook has not landed yet: never show Pay again.
    confirming() {
      return !this.paid && this.$route.query.paid === '1' && this.pollsLeft > 0;
    },
    canPay() {
      if (this.mode === 'token') return this.recipientToken.trim().length >= 16;
      const r = this.recipient;
      const a = r.recipientAddress;
      return r.recipientName.trim() && a.street.trim() && a.city.trim() && a.state.trim() && /^\d{5}(-\d{4})?$/.test(a.postalCode.trim());
    },
  },
  async created() {
    await this.load();
    // Back from Stripe: the webhook records the payment moments later.
    if (this.$route.query.paid === '1' && !this.paid) {
      this.pollsLeft = 20;
      this.poll();
    }
  },
  beforeUnmount() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
  },
  methods: {
    money(cents, currency) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format((cents || 0) / 100);
    },
    preview(line) {
      const i = line.input || {};
      const t = i.message || i.body || i.topic || i.concept || i.title;
      return t ? String(t).slice(0, 140) : '';
    },
    // The content the assistant drafted that the person may change here.
    editable(line) {
      const i = line.input || {};
      return ['message', 'body', 'title', 'topic', 'concept', 'conceptDetail', 'senderName'].filter((f) => typeof i[f] === 'string');
    },
    fieldLabel(f) {
      return { message: 'The message', body: 'The letter', title: 'Title', topic: 'What it is about', concept: 'What the piece should celebrate', conceptDetail: 'A detail that makes it personal', senderName: 'Signed by' }[f] || f;
    },
    lineInputs() {
      const out = {};
      this.cart.items.forEach((l, i) => {
        const patch = {};
        for (const f of this.editable(l)) {
          if (this.edits[i] && this.edits[i][f] !== l.input[f]) patch[f] = this.edits[i][f];
        }
        if (Object.keys(patch).length) out[i] = patch;
      });
      return out;
    },
    async load() {
      this.loading = true;
      this.error = null;
      try {
        this.cart = await gatewayGet(`/v1/carts/${encodeURIComponent(this.cartId)}/checkout`);
        this.edits = this.cart.items.map((l) => Object.fromEntries(this.editable(l).map((f) => [f, l.input[f]])));
      } catch (e) {
        this.error = e.status === 404 ? 'This cart does not exist any more.' : 'Could not load this cart right now.';
      } finally {
        this.loading = false;
      }
    },
    poll() {
      if (this.pollsLeft <= 0) return;
      this.pollTimer = setTimeout(async () => {
        try {
          this.cart = await gatewayGet(`/v1/carts/${encodeURIComponent(this.cartId)}/checkout`);
        } catch (e) {
          // keep polling
        }
        this.pollsLeft -= 1;
        if (!this.paid) this.poll();
      }, 3000);
    },
    async pay() {
      this.busy = true;
      this.error = null;
      this.details = [];
      try {
        const origin = window.location.origin;
        const payload = {
          email: this.email.trim() || undefined,
          successUrl: `${origin}/agent-checkout/${this.cartId}`,
          cancelUrl: `${origin}/agent-checkout/${this.cartId}`,
        };
        if (this.mode === 'token') payload.recipientToken = this.recipientToken.trim();
        else payload.recipient = { ...this.recipient, senderName: this.recipient.senderName.trim() || undefined };
        const lineInputs = this.lineInputs();
        if (Object.keys(lineInputs).length) payload.lineInputs = lineInputs;
        const { url } = await gatewayPost(`/v1/carts/${encodeURIComponent(this.cartId)}/checkout/session`, payload);
        window.location.assign(url);
      } catch (e) {
        this.error = e.message || 'Could not start checkout.';
        this.details = Array.isArray(e.details) ? e.details.map(String) : [];
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.agent-checkout { padding: 24px 0 64px; }
.lede { color: #555; max-width: 640px; }
.notice { padding: 12px 16px; border-radius: 8px; margin: 16px 0; }
.notice.ok { background: #e9f7ef; color: #1e5b34; }
.notice.error { background: #fdecea; color: #8a1c1c; }
.section { margin: 28px 0; }
.section h2 { font-size: 1.15rem; margin-bottom: 8px; }
.muted { color: #666; font-size: 0.95rem; }
.lines { list-style: none; padding: 0; margin: 0; }
.line { display: flex; justify-content: space-between; gap: 12px; padding: 10px 0; border-bottom: 1px solid #eee; }
.preview { color: #555; font-size: 0.9rem; margin-top: 4px; }
.line-body { flex: 1; }
.field { margin-top: 8px; }
.field label { display: block; font-size: 0.85rem; color: #666; margin-bottom: 2px; }
.field textarea { width: 100%; max-width: 520px; font: inherit; padding: 8px; border: 1px solid #ccc; border-radius: 6px; box-sizing: border-box; }
.amount { white-space: nowrap; }
.total { display: flex; justify-content: space-between; padding: 12px 0; font-size: 1.1rem; }
.tabs { display: flex; gap: 8px; margin-bottom: 12px; }
.tab { border: 1px solid #ccc; background: #fff; border-radius: 999px; padding: 6px 12px; cursor: pointer; }
.tab.on { border-color: #333; background: #f3f3f3; }
.form { display: grid; gap: 8px; max-width: 520px; margin-bottom: 8px; }
.row3 { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 8px; }
input[type="text"], input[type="email"] { font: inherit; padding: 8px; border: 1px solid #ccc; border-radius: 6px; width: 100%; box-sizing: border-box; max-width: 520px; display: block; margin-bottom: 8px; }
.fine { color: #888; font-size: 0.85rem; margin-top: 12px; }
</style>
